import React, { useState, useEffect, useCallback, useMemo, memo } from 'react';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  TextField,
  Button,
  Box,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Tooltip,
  Chip,
  Grid,
  Card,
  CardContent,
  CardMedia,
  Divider,
  Tabs,
  Tab,
  CircularProgress,
  TableSortLabel
} from '@mui/material';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as VisibilityIcon,
  Search as SearchIcon,
  FilterList as FilterIcon,
  GetApp as DownloadIcon,
  Print as PrintIcon,
  AccessTime as ClockIcon,
  ShoppingCart as CartIcon,
  ChevronLeft,
  ChevronRight,
  Add as AddIcon,
  Remove as RemoveIcon,
  CheckCircle,
  ArrowUpward,
  ArrowDownward,
  Cancel as CancelIcon,
} from '@mui/icons-material';
import { collection, query, getDocs, where, Timestamp, getDoc, doc, writeBatch, updateDoc, arrayUnion, arrayRemove, limit, startAfter, orderBy } from 'firebase/firestore';
import { db } from '../firebase';
import { format, addDays, addWeeks, addMonths, isSameDay, getWeekOfMonth, getDay, differenceInWeeks, differenceInMonths } from 'date-fns';
import { es } from 'date-fns/locale';
import { CSVLink } from 'react-csv';
import { RouteGroup, Route, Client, Order, OrderItem, InventoryItem } from '../models';
import DatePicker from 'react-datepicker';
import './OrdersTable.css';
import RefreshIcon from '@mui/icons-material/Refresh';
import styled, { keyframes } from 'styled-components';
import OrderCreationModalAutomatic from './OrderCreationModalAutomatic';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import AutorenewIcon from '@mui/icons-material/Autorenew';
import PlaylistAddIcon from '@mui/icons-material/PlaylistAdd';

const fadeIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;

const bounce = keyframes`
  0%, 20%, 50%, 80%, 100% { transform: translateY(0); }
  40% { transform: translateY(-20px); }
  60% { transform: translateY(-10px); }
`;

const DarkOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  z-index: 1300;
  animation: ${fadeIn} 0.3s ease-in-out;
`;

const NotificationPopup = styled.div`
  position: fixed;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  background-color: white;
  padding: 2rem;
  border-radius: 12px;
  z-index: 1400;
  display: flex;
  align-items: center;
  gap: 1rem;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
  animation: ${fadeIn} 0.3s ease-in-out;
`;

const AnimatedEmoji = styled.span`
  display: inline-block;
  font-size: 2rem;
  animation: ${bounce} 2s infinite;
`;

interface OrderTableItem {
  id: string;
  clientId: string;
  clientName: string;
  clientPhone: string;
  createdAt: Date;
  routeName: string;
  status: 'pending' | 'completed' | 'cancelled';
  deliveryType: 'pickup' | 'ollinItems';
  order: Order;
  routeGroup: RouteGroup;
  clientLocation: {
    provincia: string;
    canton: string;
    distrito: string;
  };
}

// Add these utility functions before the OrdersTable component
const startOfDay = (date: Date) => {
  const newDate = new Date(date);
  newDate.setHours(0, 0, 0, 0);
  return newDate;
};

const endOfDay = (date: Date) => {
  const newDate = new Date(date);
  newDate.setHours(23, 59, 59, 999);
  return newDate;
};

const generateDates = (createdAtDay: Date, oneMonthFromNow: Date, routeType: string): Date[] => {
  const dates: Date[] = [];
  let currentIterationDate = new Date(createdAtDay);

  while (currentIterationDate <= oneMonthFromNow) {
    if (routeType === 'weekly') {
      dates.push(new Date(currentIterationDate));
      currentIterationDate.setDate(currentIterationDate.getDate() + 7);
    } else if (routeType === 'biweekly') {
      dates.push(new Date(currentIterationDate));
      currentIterationDate.setDate(currentIterationDate.getDate() + 14);
    }
  }

  return dates;
};

const generateMonthlyDates = (dayOfWeek: number, weekOfMonth: number, oneMonthFromNow: Date, routeType: string): Date[] => {
  const dates: Date[] = [];
  let currentIterationDate = new Date();

  const currentDayOfWeek = currentIterationDate.getDay();
  const currentDay = currentIterationDate.getDate();
  const currentMonth = currentIterationDate.getMonth();
  const currentYear = currentIterationDate.getFullYear();
  const currentWeekOfMonth = Math.ceil(currentDay / 7);

  if (currentWeekOfMonth > weekOfMonth || (currentWeekOfMonth === weekOfMonth && currentDayOfWeek > dayOfWeek)) {
    currentIterationDate = new Date(currentYear, currentMonth + 1, 1);
  } else {
    currentIterationDate = new Date(currentYear, currentMonth, 1);
  }

  currentIterationDate.setDate((weekOfMonth - 1) * 7 + 1);
  while (currentIterationDate.getDay() !== dayOfWeek) {
    currentIterationDate.setDate(currentIterationDate.getDate() + 1);
  }

  while (currentIterationDate <= oneMonthFromNow) {
    dates.push(new Date(currentIterationDate));
    if (routeType === 'monthly') {
      currentIterationDate.setMonth(currentIterationDate.getMonth() + 1);
    } else if (routeType === 'bimonthly') {
      currentIterationDate.setMonth(currentIterationDate.getMonth() + 2);
    } else {
      currentIterationDate.setDate(currentIterationDate.getDate() + 1);
    }
  }

  return dates;
};

const locationColors: Record<string, string> = {
  'Alajuela': '#B3E5FC',
  'Cartago': '#D1C4E9',
  'Heredia': '#C8E6C9',
  'Guanacaste': '#FFF9C4',
  'Puntarenas': '#B2EBF2',
  'Limón': '#FFF59D',
  'San José': '#C5CAE9',
};

const getLocationColor = (provincia: string): string => {
  return locationColors[provincia] || 'transparent';
};

const adjustColorOpacity = (color: string, opacity: number): string => {
  if (color === 'transparent') return 'transparent';
  
  const hex = color.replace('#', '');
  const r = parseInt(hex.substring(0, 2), 16);
  const g = parseInt(hex.substring(2, 4), 16);
  const b = parseInt(hex.substring(4, 6), 16);
  
  return `rgba(${r}, ${g}, ${b}, ${opacity})`;
};

const OrdersTable: React.FC = () => {
  const [orders, setOrders] = useState<OrderTableItem[]>([]);
  const [filteredOrders, setFilteredOrders] = useState<OrderTableItem[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [isFilterDialogOpen, setIsFilterDialogOpen] = useState(false);
  const [statusFilter, setStatusFilter] = useState<string | null>(null);
  const [deliveryTypeFilter, setDeliveryTypeFilter] = useState<string | null>(null);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [clients, setClients] = useState<{ [key: string]: Client }>({});
  const [inventoryItems, setInventoryItems] = useState<InventoryItem[]>([]);
  const [selectedOrder, setSelectedOrder] = useState<OrderTableItem | null>(null);
  const [isOrderDetailsOpen, setIsOrderDetailsOpen] = useState(false);
  const [tabIndex, setTabIndex] = useState(0);
  const [dateRange, setDateRange] = useState<[Date | null, Date | null]>([addMonths(new Date(), -1), new Date()]);
  const [historyOrders, setHistoryOrders] = useState<OrderTableItem[]>([]);
  const today = new Date();
  const [startDate, setStartDate] = useState<Date | null>(today);
  const [endDate, setEndDate] = useState<Date | null>(addDays(today, 3));
  const [loading, setLoading] = useState(true); // Estado de carga
  const [showNotification, setShowNotification] = useState(false);
  const [editOrder, setEditOrder] = useState<{ routeGroupId: string; routeUnionId: string; route: Route } | null>(null);
  const [isOrderModalAutomatic, setIsOrderModalAutomatic] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [isCreateRoutesDialogOpen, setIsCreateRoutesDialogOpen] = useState(false);
  const [routesDateRange, setRoutesDateRange] = useState<[Date | null, Date | null]>([new Date(), addMonths(new Date(), 1)]);
  
  const [totalOrdersCount, setTotalOrdersCount] = useState(0);
  const [allHistoryOrders, setAllHistoryOrders] = useState<OrderTableItem[]>([]);
  
  const [routeFilter, setRouteFilter] = useState<string>('');
  const [locationFilter, setLocationFilter] = useState<string>('');
  const [statusSortOrder, setStatusSortOrder] = useState<'asc' | 'desc' | null>(null);
  
  const routeNames = useMemo(() => {
    const sourceData = allHistoryOrders.length > 0 ? allHistoryOrders : historyOrders;
    const names = new Set<string>();
    sourceData.forEach(order => {
      if (order.routeName) names.add(order.routeName);
    });
    return Array.from(names).sort();
  }, [allHistoryOrders, historyOrders]);

  const uniqueLocations = useMemo(() => {
    const sourceData = allHistoryOrders.length > 0 ? allHistoryOrders : historyOrders;
    const locations = new Set<string>();
    sourceData.forEach(order => {
      if (order.clientLocation.provincia) locations.add(order.clientLocation.provincia);
    });
    return Array.from(locations).sort();
  }, [allHistoryOrders, historyOrders]);

  // Memoize date calculations
  const [startDateMemo, endDateMemo] = useMemo(() => {
    return [startDate, endDate];
  }, [startDate, endDate]);

  // Function to normalize strings for accent-insensitive search
  const normalizeString = (str: string): string => {
    return str
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .toLowerCase();
  };

  const memoizedFilteredOrders = useMemo(() => {
    let sourceData = allHistoryOrders.length > 0 ? allHistoryOrders : historyOrders;
    
    if (routeFilter) {
      sourceData = sourceData.filter(order => order.routeName === routeFilter);
    }
    
    if (locationFilter) {
      sourceData = sourceData.filter(order => order.clientLocation.provincia === locationFilter);
    }
    
    if (!searchText.trim()) {
      setTotalOrdersCount(sourceData.length);
      return sourceData;
    }
    
    const normalizedSearch = normalizeString(searchText);
    const filteredData = sourceData.filter(order => {
      const normalizedClientName = normalizeString(order.clientName || '');
      const normalizedClientPhone = normalizeString(order.clientPhone || '');
      const normalizedDate = normalizeString(format(order.createdAt, 'dd/MM/yyyy'));
      const normalizedRouteName = normalizeString(order.routeName || '');

      return (
        normalizedClientName.includes(normalizedSearch) ||
        normalizedClientPhone.includes(normalizedSearch) ||
        normalizedDate.includes(normalizedSearch) ||
        normalizedRouteName.includes(normalizedSearch)
      );
    });
    
    setTotalOrdersCount(filteredData.length);
    
    return filteredData;
  }, [allHistoryOrders, historyOrders, searchText, routeFilter, locationFilter]);

  const sortedFilteredOrders = useMemo(() => {
    if (!statusSortOrder) return memoizedFilteredOrders;
    
    const statusPriority: Record<string, number> = {
      completed: 1,
      pending: 2,
      cancelled: 3
    };
    
    return [...memoizedFilteredOrders].sort((a, b) => {
      const priorityA = statusPriority[a.status] || 4;
      const priorityB = statusPriority[b.status] || 4;
      
      return statusSortOrder === 'asc' ? priorityA - priorityB : priorityB - priorityA;
    });
  }, [memoizedFilteredOrders, statusSortOrder]);

  // First useEffect for initial data loading - optimized with useCallback
  useEffect(() => {
    const loadInitialData = async () => {
      try {
        await Promise.all([fetchClients(), fetchInventoryItems()]);
      } catch (error) {
        console.error("Error loading initial data:", error);
      }
    };
    
    loadInitialData();
  }, []);

  // Optimized useEffect for history orders and filtering
  useEffect(() => {
    const fetchData = async () => {
      if (startDate && endDate) {
        setLoading(true);
        try {
          await fetchHistoryOrders();
        } catch (error) {
          console.error("Error fetching history orders:", error);
        } finally {
          setLoading(false);
        }
      } else if (dateRange) {
        try {
          await fetchHistoryOrders();
        } catch (error) {
          console.error("Error fetching history orders:", error);
        }
      }
    };

    fetchData();
  }, [tabIndex, dateRange, startDateMemo, endDateMemo]);

  // Update filtered orders when dependencies change
  useEffect(() => {
    setFilteredOrders(memoizedFilteredOrders);
  }, [memoizedFilteredOrders]);

  // Add these new handler functions
  const handleOpenCreateRoutesDialog = () => {
    setIsCreateRoutesDialogOpen(true);
  };

  const handleCloseCreateRoutesDialog = () => {
    setIsCreateRoutesDialogOpen(false);
  };

  const fetchClients = async () => {
    try {
      const clientsRef = collection(db, 'clients');
      const clientsSnapshot = await getDocs(clientsRef);
      const clientsData: { [key: string]: Client } = {};
      clientsSnapshot.forEach((doc) => {
        clientsData[doc.id] = { id: doc.id, ...doc.data() } as Client;
      });
      setClients(clientsData);
      console.log("Clients fetched:", clientsData);
    } catch (error) {
      console.error("Error fetching clients: ", error);
    }
  };

  const fetchInventoryItems = async () => {
    try {
      const inventoryRef = collection(db, 'inventoryItems');
      const inventorySnapshot = await getDocs(inventoryRef);
      const inventoryData: InventoryItem[] = [];
      inventorySnapshot.forEach((doc) => {
        inventoryData.push({ id: doc.id, ...doc.data() } as InventoryItem);
      });
      setInventoryItems(inventoryData);
    } catch (error) {
      console.error("Error fetching inventory items:", error);
    }
  };

  const fetchOrders = useCallback(async () => {
    try {
      const routeGroupsRef = collection(db, 'routeGroups');
      const querySnapshot = await getDocs(routeGroupsRef);

      const fetchedOrdersPromises = [];
      const clientCache = new Map();

      for (const docSnapshot of querySnapshot.docs) {
        const routeGroup = { id: docSnapshot.id, ...docSnapshot.data() } as RouteGroup;
        const routesUnionRef = collection(db, `routeGroups/${docSnapshot.id}/routesUnion`);
        const routesUnionQuery = query(
          routesUnionRef,
          where('date', '>=', startOfDay(selectedDate)),
          where('date', '<=', endOfDay(selectedDate))
        );
        
        const routesUnionSnapshot = await getDocs(routesUnionQuery);

        for (const routeUnionDoc of routesUnionSnapshot.docs) {
          const routeUnionData = routeUnionDoc.data();
          const routes = routeUnionData.routes || [];

          for (const route of routes) {
            if (route.order) {
              const orderPromise = (async () => {
                let clientData;
                if (clientCache.has(route.clientId)) {
                  clientData = clientCache.get(route.clientId);
                } else {
                  const clientDoc = await getDoc(doc(db, 'clients', route.clientId));
                  clientData = clientDoc.data() as Client;
                  clientCache.set(route.clientId, clientData);
                }

                return {
                  id: `${docSnapshot.id}-${route.id}-${routeUnionDoc.id}`,
                  clientId: route.clientId,
                  clientName: clientData?.name || 'Unknown',
                  clientPhone: clientData?.phoneNumber || 'Unknown',
                  createdAt: routeUnionData.date.toDate(),
                  routeName: routeGroup.name,
                  status: route.status,
                  deliveryType: 'ollinItems' as const,
                  order: route.order as Order,
                  routeGroup: routeGroup,
                  clientLocation: {
                    provincia: clientData?.provincia || '',
                    canton: clientData?.canton || '',
                    distrito: clientData?.distrito || ''
                  }
                };
              })();

              fetchedOrdersPromises.push(orderPromise);
            }
          }
        }
      }

      const fetchedOrders = await Promise.all(fetchedOrdersPromises);
      
      setOrders(fetchedOrders);
      setFilteredOrders(fetchedOrders);
    } catch (error) {
      console.error("Error fetching orders: ", error);
    }
  }, [selectedDate]);

  const getRouteOccurrences = (routeGroup: RouteGroup, date: Date): Date[] => {
    const occurrences: Date[] = [];
    const createdAt = routeGroup.createdAt instanceof Timestamp ? routeGroup.createdAt.toDate() : new Date(routeGroup.createdAt);

    switch (routeGroup.routeType) {
      case 'weekly':
        if (createdAt <= date) {
          let currentDate = createdAt;
          while (currentDate <= date) {
            if (isSameDay(currentDate, date)) {
              occurrences.push(new Date(currentDate));
            }
            currentDate = addWeeks(currentDate, 1);
          }
        }
        break;
      case 'biweekly':
        if (createdAt <= date) {
          let currentDate = createdAt;
          while (currentDate <= date) {
            if (isSameDay(currentDate, date)) {
              occurrences.push(new Date(currentDate));
            }
            currentDate = addWeeks(currentDate, 2);
          }
        }
        break;
      case 'monthly':
        if (getDay(date) === routeGroup.dayOfWeek && getWeekOfMonth(date) === routeGroup.weekOfMonth) {
          occurrences.push(new Date(date));
        }
        break;
    }

    return occurrences;
  };

  const getRouteOccurrencesHistory = (routeGroup: RouteGroup, startDate: Date | null, endDate: Date | null): Date[] => {
    const occurrences: Date[] = [];
    if (!startDate || !endDate) return occurrences;

    const createdAt = routeGroup.createdAt instanceof Timestamp ? routeGroup.createdAt.toDate() : new Date(routeGroup.createdAt);

    switch (routeGroup.routeType) {
      case 'weekly':
        if (createdAt <= endDate) {
          let currentDate = createdAt;
          while (currentDate <= endDate) {
            if (currentDate >= startDate) {
              occurrences.push(new Date(currentDate));
            }
            currentDate = addWeeks(currentDate, 1);
          }
        }
        break;
      case 'biweekly':
        if (createdAt <= endDate) {
          let currentDate = createdAt;
          while (currentDate <= endDate) {
            if (currentDate >= startDate) {
              occurrences.push(new Date(currentDate));
            }
            currentDate = addWeeks(currentDate, 2);
          }
        }
        break;
      case 'monthly':
        let currentDate = createdAt;
        while (currentDate <= endDate) {
          if (currentDate >= startDate && getDay(currentDate) === routeGroup.dayOfWeek && getWeekOfMonth(currentDate) === routeGroup.weekOfMonth) {
            occurrences.push(new Date(currentDate));
          }
          currentDate = addMonths(currentDate, 1);
        }
        break;
    }

    return occurrences;
  };

  const applyFilters = () => {
    console.log("Applying filters. Current orders:", orders);
    console.log("Search term:", searchTerm);
    console.log("Status filter:", statusFilter);
    console.log("Delivery type filter:", deliveryTypeFilter);

    let filtered = orders.filter(order =>
      order.clientName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      order.clientPhone.includes(searchTerm)
    );

    if (statusFilter) {
      filtered = filtered.filter(order => order.status === statusFilter);
    }

    if (deliveryTypeFilter) {
      filtered = filtered.filter(order => order.deliveryType === deliveryTypeFilter);
    }

    console.log("Filtered orders:", filtered);
    setFilteredOrders(filtered);
  };

  const handleSearch = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(event.target.value);
  };

  const handleOpenFilterDialog = () => {
    setIsFilterDialogOpen(true);
  };

  const handleCloseFilterDialog = () => {
    setIsFilterDialogOpen(false);
  };

  const handleApplyFilters = () => {
    applyFilters();
    handleCloseFilterDialog();
  };

  const handleResetFilters = () => {
    setStatusFilter(null);
    setDeliveryTypeFilter(null);
    setSearchTerm('');
    setFilteredOrders(orders);
    handleCloseFilterDialog();
  };

  const handlePreviousDay = () => {
    const newDate = addDays(selectedDate, -1);
    setSelectedDate(newDate);
  };

  const handleNextDay = () => {
    const newDate = addDays(selectedDate, 1);
    setSelectedDate(newDate);
  };

  const handlePrint = () => {
    window.print();
  };

  const handleViewOrder = (order: OrderTableItem) => {
    setSelectedOrder(order);
    setIsOrderDetailsOpen(true);
  };

  const handleCloseOrderDetails = () => {
    setIsOrderDetailsOpen(false);
    setSelectedOrder(null);
  };

  const csvData = memoizedFilteredOrders.map(order => ({
    ID: order.id,
    Nombre: order.clientName,
    Teléfono: order.clientPhone,
    'Fecha de Pedido': format(order.createdAt, 'dd/MM/yyyy'),
    'Nombre de Ruta': order.routeName,
    Estado: order.status,
    'Tipo de Entrega': order.deliveryType
  }));

  const fetchHistoryOrders = async () => {
    try {
      setLoading(true);
      
      if (!startDate || !endDate) {
        setLoading(false);
        return;
      }

      const startDateTime = new Date(startDate);
      startDateTime.setHours(0, 0, 0, 0);

      const endDateTime = new Date(endDate);
      endDateTime.setHours(23, 59, 59, 999);

      const routeGroupsRef = collection(db, 'routeGroups');
      const querySnapshot = await getDocs(routeGroupsRef);

      const allRoutesData: Array<{
        routeGroup: RouteGroup;
        routeUnionDocId: string;
        route: any;
        orderDate: Date;
      }> = [];

      const routesPromises = querySnapshot.docs.map(async (docSnapshot) => {
        const routeGroup = { id: docSnapshot.id, ...docSnapshot.data() } as RouteGroup;

        const routesUnionRef = collection(db, `routeGroups/${docSnapshot.id}/routesUnion`);
        const routesUnionQuery = query(
          routesUnionRef,
          where('date', '>=', startDateTime),
          where('date', '<=', endDateTime),
          orderBy('date', 'desc')
        );
        const routesUnionSnapshot = await getDocs(routesUnionQuery);

        const routesForGroup: Array<{
          routeGroup: RouteGroup;
          routeUnionDocId: string;
          route: any;
          orderDate: Date;
        }> = [];

        for (const routeUnionDoc of routesUnionSnapshot.docs) {
          const routeUnionData = routeUnionDoc.data();
          const routes = routeUnionData.routes || [];

          for (const route of routes) {
            if (route.order) {
              const orderDate = routeUnionData.date.toDate();
              if (orderDate >= startDateTime && orderDate <= endDateTime) {
                routesForGroup.push({
                  routeGroup,
                  routeUnionDocId: routeUnionDoc.id,
                  route,
                  orderDate
                });
              }
            }
          }
        }

        return routesForGroup;
      });

      const allRoutesResults = await Promise.all(routesPromises);
      allRoutesResults.forEach(routes => allRoutesData.push(...routes));

      allRoutesData.sort((a, b) => b.orderDate.getTime() - a.orderDate.getTime());

      const clientIdsArray = allRoutesData.map(d => d.route.clientId);
      const uniqueClientIds = clientIdsArray.filter((id, index) => clientIdsArray.indexOf(id) === index);
      
      const clientsMap: { [key: string]: Client } = {};
      const clientFetchPromises = uniqueClientIds.map(async (clientId) => {
        try {
          const clientDoc = await getDoc(doc(db, 'clients', clientId));
          if (clientDoc.exists()) {
            clientsMap[clientId] = { id: clientDoc.id, ...clientDoc.data() } as Client;
          }
        } catch (e) {
          console.error('Error fetching client:', clientId, e);
        }
      });
      
      await Promise.all(clientFetchPromises);

      const allFetchedOrders: OrderTableItem[] = allRoutesData.map((data) => {
        const clientData = clientsMap[data.route.clientId];

        return {
          id: `${data.routeGroup.id}-${data.route.id}-${data.routeUnionDocId}-${data.orderDate.getTime()}`,
          clientId: data.route.clientId,
          clientName: clientData ? clientData.name : 'Unknown',
          clientPhone: clientData ? clientData.phoneNumber : 'Unknown',
          createdAt: data.orderDate,
          routeName: data.routeGroup.name,
          status: data.route.status,
          deliveryType: 'ollinItems',
          order: data.route.order as Order,
          routeGroup: data.routeGroup,
          clientLocation: {
            provincia: clientData?.provincia || '',
            canton: clientData?.canton || '',
            distrito: clientData?.distrito || ''
          }
        };
      });

      setTotalOrdersCount(allFetchedOrders.length);
      setAllHistoryOrders(allFetchedOrders);
      setHistoryOrders(allFetchedOrders);
      setFilteredOrders(allFetchedOrders);
      
      setLoading(false);
    } catch (error) {
      console.error("Error fetching history orders: ", error);
      setLoading(false);
    }
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabIndex(newValue);
  };

  const handleCreateRoutesUnion = useCallback(async () => {
    setShowNotification(true);
    setIsCreateRoutesDialogOpen(false);

    try {
      const initialDate = routesDateRange[0] ? new Date(routesDateRange[0]) : new Date();
      const finalDate = routesDateRange[1] ? new Date(routesDateRange[1]) : addMonths(initialDate, 1);
console.log("Initial Date:", initialDate, "Final Date:", finalDate);
      const routeGroupsRef = collection(db, 'routeGroups');
      const routeGroupsQuery = query(
        routeGroupsRef,
        where('routeType', 'in', ['weekly', 'biweekly', 'monthly', 'bimonthly'])
      );
      const routeGroupsSnapshot = await getDocs(routeGroupsQuery);

      for (const routeGroupDoc of routeGroupsSnapshot.docs) {
        const routeGroupData = routeGroupDoc.data() as RouteGroup;
        const routeType = routeGroupData.routeType;

        // Fetch existing routesUnion for date comparison
        const routesUnionQuery = query(
          collection(db, `routeGroups/${routeGroupDoc.id}/routesUnion`),
          where('date', '>=', initialDate),
          where('date', '<=', finalDate)
        );
        const routesUnionSnapshot = await getDocs(routesUnionQuery);

        // Create a map of existing dates
        const existingDates = new Map();
        routesUnionSnapshot.docs.forEach(doc => {
          const date = doc.data().date.toDate();
          const dateKey = `${date.getFullYear()}-${date.getMonth()}-${date.getDate()}`;
          existingDates.set(dateKey, true);
        });

        // Fetch routes
        const routesRef = collection(db, `routeGroups/${routeGroupDoc.id}/routes`);
        const routesSnapshot = await getDocs(routesRef);
        const routes: Route[] = routesSnapshot.docs.map(routeDoc => ({
          id: routeDoc.id,
          ...routeDoc.data()
        } as Route));

        const createdAtTimestamp = routeGroupData.createdAt;
        const createdAt = createdAtTimestamp instanceof Timestamp ? createdAtTimestamp.toDate() : new Date(createdAtTimestamp);

        if (isNaN(createdAt.getTime())) {
          continue;
        }

        const datesToCreate = [];
        let currentDate = new Date(initialDate);

        // Función auxiliar para obtener la fecha del n-ésimo día de la semana en un mes
        const getNthDayOfMonth = (date: Date, dayOfWeek: number, weekOfMonth: number): Date => {
          const firstDayOfMonth = new Date(date.getFullYear(), date.getMonth(), 1);
          let targetDay = firstDayOfMonth.getDay();
          let daysToAdd = dayOfWeek - targetDay;
          if (daysToAdd < 0) daysToAdd += 7;
          daysToAdd += (weekOfMonth - 1) * 7;
          return new Date(date.getFullYear(), date.getMonth(), 1 + daysToAdd);
        };

        while (startOfDay(currentDate) <= endOfDay(finalDate)) {
          const shouldCreateRoute = (() => {
            switch (routeType) {
              case 'weekly':
                return getDay(currentDate) === getDay(createdAt);
              case 'biweekly': {
                // Obtener el día de la semana correcto
                const isSameWeekDay = getDay(currentDate) === getDay(createdAt);
                if (!isSameWeekDay) return false;

                // Calcular si estamos en una semana par desde la fecha de creación
                const startWeek = startOfDay(createdAt);
                const currentWeek = startOfDay(currentDate);
                const weekDiff = differenceInWeeks(currentWeek, startWeek);
                return weekDiff % 2 === 0;
              }
              case 'monthly': {
                const targetDate = getNthDayOfMonth(currentDate, routeGroupData.dayOfWeek || 0, routeGroupData.weekOfMonth || 1);
                return isSameDay(currentDate, targetDate);
              }
              case 'bimonthly': {
                // Primero verificar si es el día correcto del mes
                const targetDate = getNthDayOfMonth(currentDate, routeGroupData.dayOfWeek || 0, routeGroupData.weekOfMonth || 1);
                if (!isSameDay(currentDate, targetDate)) return false;

                // Luego verificar si estamos en un mes par desde la fecha de creación
                const startMonth = new Date(createdAt.getFullYear(), createdAt.getMonth(), 1);
                const currentMonth = new Date(currentDate.getFullYear(), currentDate.getMonth(), 1);
                const monthDiff = differenceInMonths(currentMonth, startMonth);
                return monthDiff % 2 === 0;
              }
              default:
                return false;
            }
          })();

          if (shouldCreateRoute) {
            const dateKey = `${currentDate.getFullYear()}-${currentDate.getMonth()}-${currentDate.getDate()}`;
            if (!existingDates.has(dateKey)) {
              const normalizedDate = new Date(
                currentDate.getFullYear(),
                currentDate.getMonth(),
                currentDate.getDate(),
                0, 0, 0, 0
              );
              datesToCreate.push(normalizedDate);
            }
          }

          // Avanzar al siguiente día
          currentDate = addDays(currentDate, 1);
        }

        // Crear los documentos para las fechas seleccionadas
        if (datesToCreate.length > 0) {
          const batch = writeBatch(db);
          for (const date of datesToCreate) {
            const newRoutesUnionRef = doc(collection(db, `routeGroups/${routeGroupDoc.id}/routesUnion`));
            batch.set(newRoutesUnionRef, {
              routes,
              date: date,
              isUnificationFlag: false,
            });
          }
          await batch.commit();
        }
      }

      setLoading(false);
    } catch (error) {
      console.error("Error creating routes:", error);
      setLoading(false);
    } finally {
      setShowNotification(false);
      if (routesDateRange[0] && routesDateRange[1]) {
        setLoading(true);
        fetchHistoryOrders();
      }
    }
  }, [routesDateRange, fetchHistoryOrders]);

  const handleStartDateChange = (date: Date | null) => {
    setStartDate(date);
    setLoading(true); // Set loading when date changes
  };

  const handleEndDateChange = (date: Date | null) => {
    setEndDate(date);
    setLoading(true); // Set loading when date changes
  };

  const handleEditOrder = async (order: OrderTableItem) => {
    const [routeGroupId, routeId, routeUnionId] = order.id.split('-');

    if (routeGroupId && routeUnionId) {
      const routesUnionRef = doc(db, `routeGroups/${routeGroupId}/routesUnion`, routeUnionId);
      const routesUnionDoc = await getDoc(routesUnionRef);
      const routesUnionData = routesUnionDoc.data();

      // Find route by routeId and clientId
      const currentRoute = routesUnionData?.routes?.find((r: Route) =>
        r.clientId === order.clientId
      ) || {};

      if (currentRoute && currentRoute.clientId) {
        setEditOrder({
          routeGroupId,
          routeUnionId,
          route: {
            id: routeId || '',
            clientId: currentRoute?.clientId || '',
            status: currentRoute?.status || '',
            order: currentRoute?.order || '',
            tipo_pedido: 'Automatizado',
            fecha_pedido: currentRoute?.fecha_pedido || '',
            routeGroupId: routeGroupId,
            note_admin: currentRoute?.note_admin || '',
            note_driver: currentRoute?.note_driver || '',
            deliveryFileUrl: currentRoute?.deliveryFileUrl || '',
            container_values: currentRoute?.container_values || [],
            lenght_container_values: currentRoute?.container_values?.length || 0
          }
        });
        setIsOrderModalAutomatic(true);
      }
    }
  };

  const handleOrderCreated = async (newOrder: Order, noteAdmin?: string, noteDriver?: string, deliveryFileUrl?: string, container_values?: string[], lenght_container_values?: string) => {
    if (editOrder) {
      try {
        const routesUnionRef = doc(db, `routeGroups/${editOrder.routeGroupId}/routesUnion`, editOrder.routeUnionId);
        const routesUnionDoc = await getDoc(routesUnionRef);

        if (routesUnionDoc.exists()) {
          const routesUnionData = routesUnionDoc.data();
          const oldRoute = routesUnionData.routes.find((route: Route) => route.id === editOrder.route.id);
          const updatedRoute = {
            ...oldRoute,
            order: newOrder,
            note_admin: noteAdmin || '',
            note_driver: noteDriver || '',
            container_values: container_values || [],
            lenght_container_values: lenght_container_values || 0,
            deliveryFileUrl: deliveryFileUrl || ''
          };

          // Actualizar solo la ruta específica usando arrayRemove y arrayUnion
          await updateDoc(routesUnionRef, {
            routes: arrayRemove(oldRoute)
          });
          await updateDoc(routesUnionRef, {
            routes: arrayUnion(updatedRoute)
          });

          toast.success('Orden actualizada exitosamente');
        }
      } catch (error) {
        console.error("Error updating order:", error);
        toast.error('Error al actualizar la orden');
      } finally {
        setIsOrderModalAutomatic(false);
        setEditOrder(null);
      }
    }
  };

  return (
    <Box sx={{ padding: 3 }}>
      <Box sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        flexShrink: 0,
        color: 'rgb(0, 180, 216)'
      }}>
        <h1>Gestión de Clientes</h1>
      </Box>
      {/* linea horizontal de lado a lado con un ancho de 4px que sea de color rgb(0, 180, 216) */}
      <div style={{
        width: '100%',
        height: '4px',
        backgroundColor: 'rgb(0, 180, 216)',
        margin: '20px 0'
      }} />
      {/* <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Button
          onClick={handleCreateRoutesUnion}
          color="primary"
          sx={{
            color: 'white',
            backgroundColor: '#87CEEB', // Color de fondo atractivo
            borderRadius: '8px', // Bordes redondeados
            padding: '10px', // Espaciado interno
            boxShadow: '0px 4px 8px rgba(0, 0, 0, 0.2)', // Sombra para efecto de elevación
            '&:hover': {
              backgroundColor: '#5CACEE', // Color de fondo al pasar el mouse
              boxShadow: '0px 6px 12px rgba(0, 0, 0, 0.3)', // Sombra más intensa al pasar el mouse
            },
            '&:active': {
              backgroundColor: '#4DA3D1', // Color de fondo al hacer clic
              transform: 'scale(0.95)', // Efecto de pulsación al hacer clic
            },
            fontWeight: 'bold',
            transition: 'background-color 0.3s ease, transform 0.2s ease, box-shadow 0.3s ease', // Transiciones suaves
          }}
          title="Actualizar datos" // Tooltip para mejorar la accesibilidad
        >
          <RefreshIcon fontSize="large" sx={{ mr: '6px', fontSize: '1.5rem' }} />
          Crear Rutas Perzonalizadas Disponibles
        </Button>
      </Box> */}

      {tabIndex === 0 && (
        <>
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', mb: 3, flexDirection: 'column' }}>
            <Box sx={{ mb: 2 }}>
              <Typography variant="h6" sx={{ color: 'rgb(0, 180, 216)', fontWeight: 'bold' }}>Selecciona el rango de fechas</Typography>
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 2 }}>
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
                <label style={{ color: 'rgb(0, 180, 216)', fontWeight: 'bold' }}>Fecha de inicio</label>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <IconButton onClick={() => handleStartDateChange(addMonths(startDate || new Date(), -1))}>
                    <ChevronLeft />
                  </IconButton>
                  <DatePicker
                    selected={startDate}
                    onChange={handleStartDateChange}
                    selectsStart
                    startDate={startDate || undefined}
                    endDate={endDate || undefined}
                    dateFormat="dd/MM/yyyy"
                    customInput={<TextField variant="outlined" sx={{ width: '300px', input: { color: 'rgb(0, 180, 216)' } }} />}
                    locale={es}
                    popperModifiers={[
                      {
                        name: 'offset',
                        options: {
                          offset: [0, 10],
                        },
                        fn: ({ x, y, ...rest }) => {
                          return { x, y, ...rest };
                        },
                      },
                      {
                        name: 'preventOverflow',
                        options: {
                          rootBoundary: 'viewport',
                          tether: false,
                          altAxis: true,
                        },
                        fn: ({ x, y, ...rest }) => {
                          return { x, y, ...rest };
                        },
                      },
                    ]}
                    calendarClassName="custom-calendar"
                  />
                  <IconButton onClick={() => handleStartDateChange(addMonths(startDate || new Date(), 1))}>
                    <ChevronRight />
                  </IconButton>
                </Box>
              </Box>
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
                <label style={{ color: 'rgb(0, 180, 216)', fontWeight: 'bold' }}>Fecha de fin</label>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <IconButton onClick={() => handleEndDateChange(addMonths(endDate || new Date(), -1))}>
                    <ChevronLeft />
                  </IconButton>
                  <DatePicker
                    selected={endDate}
                    onChange={handleEndDateChange}
                    selectsEnd
                    startDate={startDate || undefined}
                    endDate={endDate || undefined}
                    minDate={startDate || undefined}
                    dateFormat="dd/MM/yyyy"
                    customInput={<TextField variant="outlined" sx={{ width: '300px', input: { color: 'rgb(0, 180, 216)' } }} />}
                    locale={es}
                    popperModifiers={[
                      {
                        name: 'offset',
                        options: {
                          offset: [0, 10],
                        },
                        fn: ({ x, y, ...rest }) => {
                          return { x, y, ...rest };
                        },
                      },
                      {
                        name: 'preventOverflow',
                        options: {
                          rootBoundary: 'viewport',
                          tether: false,
                          altAxis: true,
                        },
                        fn: ({ x, y, ...rest }) => {
                          return { x, y, ...rest };
                        },
                      },
                    ]}
                    calendarClassName="custom-calendar"
                  />
                  <IconButton onClick={() => handleEndDateChange(addMonths(endDate || new Date(), 1))}>
                    <ChevronRight />
                  </IconButton>
                </Box>
              </Box>
            </Box>
          </Box>

          <Box sx={{ mb: 3, width: '100%', display: 'flex', gap: 2, alignItems: 'center' }}>
            <TextField
              fullWidth
              variant="outlined"
              placeholder="Buscar por Nombre, Teléfono, Fecha o Nombre de Ruta"
              value={searchText}
              InputProps={{
                startAdornment: (
                  <SearchIcon sx={{ color: 'rgb(0, 180, 216)', mr: 1 }} />
                ),
              }}
              onChange={(e) => setSearchText(e.target.value)}
              sx={{
                width: '40%',
                '& .MuiOutlinedInput-root': {
                  '& fieldset': {
                    borderColor: 'rgb(0, 180, 216)',
                  },
                  '&:hover fieldset': {
                    borderColor: 'rgb(0, 180, 216)',
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: 'rgb(0, 180, 216)',
                  },
                },
              }}
            />
            <FormControl sx={{ width: '20%', minWidth: 160 }}>
              <InputLabel sx={{ color: 'rgb(0, 180, 216)', '&.Mui-focused': { color: 'rgb(0, 180, 216)' } }}>
                Ubicación
              </InputLabel>
              <Select
                value={locationFilter}
                onChange={(e) => setLocationFilter(e.target.value)}
                label="Ubicación"
                MenuProps={{
                  PaperProps: {
                    sx: {
                      maxHeight: 300,
                      width: 'auto',
                      minWidth: 160,
                    }
                  },
                  anchorOrigin: {
                    vertical: 'bottom',
                    horizontal: 'left',
                  },
                  transformOrigin: {
                    vertical: 'top',
                    horizontal: 'left',
                  },
                }}
                sx={{
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: 'rgb(0, 180, 216)',
                  },
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: 'rgb(0, 180, 216)',
                  },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    borderColor: 'rgb(0, 180, 216)',
                  },
                }}
              >
                <MenuItem value="">Todas</MenuItem>
                {uniqueLocations.map((location) => (
                  <MenuItem key={location} value={location}>{location}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl sx={{ width: '25%', minWidth: 200 }}>
              <InputLabel sx={{ color: 'rgb(0, 180, 216)', '&.Mui-focused': { color: 'rgb(0, 180, 216)' } }}>
                Filtrar por Ruta
              </InputLabel>
              <Select
                value={routeFilter}
                onChange={(e) => setRouteFilter(e.target.value)}
                label="Filtrar por Ruta"
                MenuProps={{
                  PaperProps: {
                    sx: {
                      maxHeight: 300,
                      width: 'auto',
                      minWidth: 200,
                    }
                  },
                  anchorOrigin: {
                    vertical: 'bottom',
                    horizontal: 'left',
                  },
                  transformOrigin: {
                    vertical: 'top',
                    horizontal: 'left',
                  },
                }}
                sx={{
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: 'rgb(0, 180, 216)',
                  },
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: 'rgb(0, 180, 216)',
                  },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    borderColor: 'rgb(0, 180, 216)',
                  },
                }}
              >
                <MenuItem value="">Todas las Rutas</MenuItem>
                {routeNames.map((routeName) => (
                  <MenuItem key={routeName} value={routeName}>{routeName}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <Button
              onClick={handleOpenCreateRoutesDialog}
              color="primary"
              sx={{
                marginLeft: 'auto',
                color: 'white',
                backgroundColor: '#87CEEB',
                borderRadius: '8px',
                padding: '10px',
                boxShadow: '0px 4px 8px rgba(0, 0, 0, 0.2)',
                '&:hover': {
                  backgroundColor: '#5CACEE',
                  boxShadow: '0px 6px 12px rgba(0, 0, 0, 0.3)',
                },
                '&:active': {
                  backgroundColor: '#4DA3D1',
                  transform: 'scale(0.95)',
                },
                fontWeight: 'bold',
                transition: 'background-color 0.3s ease, transform 0.2s ease, box-shadow 0.3s ease',
              }}
            >
              <PlaylistAddIcon fontSize="large" sx={{ mr: '6px', fontSize: '1.5rem' }} />
              Crear Rutas Disponibles
            </Button>
          </Box>

          <TableContainer component={Paper} sx={{ boxShadow: 3, borderRadius: 2, minHeight: 'calc(60vh)' }}>
            <Table sx={{ minWidth: 900 }} aria-label="simple table">
              <TableHead sx={{ backgroundColor: '#87CEEB' }}>
                <TableRow>
                  <TableCell 
                    sx={{ 
                      color: 'white', 
                      fontWeight: 'bold',
                      cursor: 'pointer',
                      '&:hover': { backgroundColor: 'rgba(255,255,255,0.1)' }
                    }}
                    onClick={() => setStatusSortOrder(statusSortOrder === 'asc' ? 'desc' : statusSortOrder === 'desc' ? null : 'asc')}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      Estado
                      {statusSortOrder === 'asc' && <ArrowUpward sx={{ fontSize: 16 }} />}
                      {statusSortOrder === 'desc' && <ArrowDownward sx={{ fontSize: 16 }} />}
                    </Box>
                  </TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Tipo</TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Nombre</TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Tel</TableCell>
                  <TableCell 
                    sx={{ 
                      color: 'white', 
                      fontWeight: 'bold',
                      cursor: 'pointer',
                      backgroundColor: locationFilter ? 'rgba(255,255,255,0.3)' : 'transparent',
                      '&:hover': { backgroundColor: 'rgba(255,255,255,0.2)' }
                    }}
                    onClick={() => {
                      if (!locationFilter) {
                        if (uniqueLocations.length > 0) {
                          setLocationFilter(uniqueLocations[0]);
                        }
                      } else {
                        const currentIndex = uniqueLocations.indexOf(locationFilter);
                        if (currentIndex < uniqueLocations.length - 1) {
                          setLocationFilter(uniqueLocations[currentIndex + 1]);
                        } else {
                          setLocationFilter('');
                        }
                      }
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      Ubicación
                      {locationFilter && <FilterIcon sx={{ fontSize: 14 }} />}
                    </Box>
                  </TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Fecha de Pedido</TableCell>
                  <TableCell 
                    sx={{ 
                      color: 'white', 
                      fontWeight: 'bold',
                      cursor: 'pointer',
                      backgroundColor: routeFilter ? 'rgba(255,255,255,0.3)' : 'transparent',
                      '&:hover': { backgroundColor: 'rgba(255,255,255,0.2)' }
                    }}
                    onClick={() => {
                      if (!routeFilter) {
                        if (routeNames.length > 0) {
                          setRouteFilter(routeNames[0]);
                        }
                      } else {
                        const currentIndex = routeNames.indexOf(routeFilter);
                        if (currentIndex < routeNames.length - 1) {
                          setRouteFilter(routeNames[currentIndex + 1]);
                        } else {
                          setRouteFilter('');
                        }
                      }
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      Nombre de Ruta
                      {routeFilter && <FilterIcon sx={{ fontSize: 14 }} />}
                    </Box>
                  </TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Acciones</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={9} sx={{
                      height: '400px',
                      border: 'none'
                    }}
                    >
                      <Box
                        sx={{
                          display: 'flex',
                          height: '100%',
                          width: '100%',
                          alignItems: 'center',
                          justifyContent: 'center'
                        }}
                      >
                        <CircularProgress size={60} />
                      </Box>
                    </TableCell>
                  </TableRow>
                ) : sortedFilteredOrders.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={9} sx={{ textAlign: 'center' }}>
                      <Typography>
                        {searchText.trim() || routeFilter
                          ? 'No se encontraron resultados para la búsqueda'
                          : 'No hay pedidos para mostrar en el rango de fechas seleccionado'}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  sortedFilteredOrders
                    .map((order) => {
                      const locationColor = getLocationColor(order.clientLocation.provincia);
                      return (
                      <TableRow key={order.id}
                        sx={{
                          backgroundColor: order.clientLocation.provincia ? locationColor : 'transparent',
                          '&:hover': {
                            backgroundColor: order.clientLocation.provincia 
                              ? adjustColorOpacity(locationColor, 0.7)
                              : 'rgba(0, 180, 216, 0.1)',
                            '& .edit-icon': {
                              opacity: 1,
                            },
                          },
                        }}>
                        <TableCell>
                          {order.status === 'completed' ? (
                            <Tooltip title="Completado">
                              <CheckCircle color="success" />
                            </Tooltip>
                          ) : order.status === 'cancelled' ? (
                            <Tooltip title="Cancelado">
                              <CancelIcon color="error" />
                            </Tooltip>
                          ) : (
                            <Tooltip title="Pendiente">
                              <ClockIcon color="action" />
                            </Tooltip>
                          )}
                        </TableCell>
                        <TableCell>
                          <CartIcon color="primary" />
                        </TableCell>
                        <TableCell sx={{ fontWeight: order.clientLocation.provincia ? 600 : 400 }}>{order.clientName}</TableCell>
                        <TableCell>{order.clientPhone}</TableCell>
                        <TableCell>
                          <Typography 
                            variant="body2" 
                            sx={{ 
                              fontSize: '0.75rem',
                              cursor: order.clientLocation.provincia ? 'pointer' : 'default',
                              '&:hover': order.clientLocation.provincia ? { textDecoration: 'underline', color: 'rgb(0, 180, 216)' } : {}
                            }}
                            onClick={(e) => {
                              e.stopPropagation();
                              if (order.clientLocation.provincia) {
                                setLocationFilter(order.clientLocation.provincia);
                              }
                            }}
                          >
                            {order.clientLocation.provincia && (
                              <><strong>{order.clientLocation.provincia}</strong><br /></>
                            )}
                            {order.clientLocation.canton && (
                              <>{order.clientLocation.canton}<br /></>
                            )}
                            {order.clientLocation.distrito && (
                              <>{order.clientLocation.distrito}</>
                            )}
                            {!order.clientLocation.provincia && '-'}
                          </Typography>
                        </TableCell>
                        <TableCell>{format(order.createdAt, 'dd/MM/yyyy')}</TableCell>
                        <TableCell 
                          sx={{ 
                            cursor: 'pointer',
                            '&:hover': { textDecoration: 'underline', color: 'rgb(0, 180, 216)' }
                          }}
                          onClick={() => setRouteFilter(order.routeName)}
                        >
                          {order.routeName}
                        </TableCell>
                        <TableCell>
                          <IconButton
                            size="small"
                            aria-label="ver"
                            onClick={() => handleViewOrder(order)}
                            sx={{
                              color: '#00b4d8',
                              '&:hover': {
                                backgroundColor: 'rgba(0, 180, 216, 0.1)',
                              },
                            }}
                          >
                            <VisibilityIcon sx={{ color: 'rgb(0, 180, 216)' }} />
                          </IconButton>
                          {/* <IconButton
                            size="small"
                            aria-label="editar"
                            onClick={() => handleEditOrder(order)}
                            sx={{
                              color: '#00b4d8',
                              '&:hover': {
                                backgroundColor: 'rgba(0, 180, 216, 0.1)',
                              },
                            }}
                          >
                            <EditIcon sx={{ color: 'rgb(0, 180, 216)' }} />
                          </IconButton> */}
                        </TableCell>
                      </TableRow>
                      );
                    })
                )}
              </TableBody>
            </Table>

            {sortedFilteredOrders.length > 0 && (
              <Box sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                p: 2,
                gap: 2
              }}>
                <Typography variant="caption" color="textSecondary">
                  Total: {totalOrdersCount} pedidos
                </Typography>
              </Box>
            )}
          </TableContainer>
        </>
      )}

      <Dialog open={isFilterDialogOpen} onClose={handleCloseFilterDialog}>
        <DialogTitle sx={{ backgroundColor: '#87CEEB', color: 'white', fontWeight: 'bold' }}>
          Filtrar Pedidos
        </DialogTitle>
        <DialogContent>
          <FormControl fullWidth margin="normal">
            <InputLabel>Estado</InputLabel>
            <Select
              value={statusFilter || ''}
              onChange={(e) => setStatusFilter(e.target.value as string | null)}
            >
              <MenuItem value="">Todos</MenuItem>
              <MenuItem value="pending">Pendiente</MenuItem>
              <MenuItem value="completed">Completado</MenuItem>
              <MenuItem value="cancelled">Cancelado</MenuItem>
            </Select>
          </FormControl>
          <FormControl fullWidth margin="normal">
            <InputLabel>Tipo de Entrega</InputLabel>
            <Select
              value={deliveryTypeFilter || ''}
              onChange={(e) => setDeliveryTypeFilter(e.target.value as string | null)}
            >
              <MenuItem value="">Todos</MenuItem>
              <MenuItem value="pickup">Recolección</MenuItem>
              <MenuItem value="ollinItems">Ollin Items</MenuItem>
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions sx={{
          justifyContent: 'center',
          px: 3,
          py: 2,
          backgroundColor: 'rgba(135, 206, 235, 0.1)'
        }}>
          <Button onClick={handleResetFilters}
            variant="outlined"
            sx={{
              color: '#87CEEB',
              borderColor: '#87CEEB',
              '&:hover': {
                backgroundColor: 'rgba(135, 206, 235, 0.1)',
                borderColor: '#5CACEE',
              },
              fontWeight: 'bold',
              px: 4,
              py: 1,
              mr: 2,
            }}>Resetear</Button>
          <Button onClick={handleApplyFilters}
            variant="contained"
            sx={{
              backgroundColor: '#87CEEB',
              color: 'white',
              '&:hover': {
                backgroundColor: '#5CACEE',
              },
              fontWeight: 'bold',
              px: 4,
              py: 1,
            }}>Aplicar</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={isOrderDetailsOpen} onClose={handleCloseOrderDetails} maxWidth="md" fullWidth>
        <DialogTitle sx={{ backgroundColor: '#87CEEB', color: 'white', fontWeight: 'bold' }}>
          Detalles del Pedido
        </DialogTitle>
        <DialogContent>
          {selectedOrder && (
            <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%', padding: 2 }}>
              <Grid container spacing={2} sx={{ mb: 2 }}>
                <Grid item xs={12} sm={6} md={3}>
                  <Typography variant="subtitle2" color="text.secondary">Cliente</Typography>
                  <Typography variant="subtitle1" fontWeight="bold">{selectedOrder.clientName}</Typography>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Typography variant="subtitle2" color="text.secondary">Estado</Typography>
                  <Typography variant="subtitle1" fontWeight="bold">{selectedOrder.status}</Typography>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Typography variant="subtitle2" color="text.secondary">Fecha</Typography>
                  <Typography variant="subtitle1" fontWeight="bold">{format(selectedOrder.createdAt, 'dd/MM/yyyy')}</Typography>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Typography variant="subtitle2" color="text.secondary">Ruta</Typography>
                  <Typography variant="subtitle1" fontWeight="bold">{selectedOrder.routeName}</Typography>
                </Grid>
              </Grid>
              <Divider sx={{ my: 2 }} />
              <Typography variant="h6" sx={{ mt: 2, mb: 1, color: '#007BFF' }}>
                Ítems del Pedido
              </Typography>
              <Grid container spacing={2}>
                {selectedOrder.order.items.map((item: OrderItem) => {
                  const inventoryItem = inventoryItems.find(invItem => invItem.id === item.inventoryItemId);
                  return (
                    <Grid item xs={12} sm={6} md={4} key={item.inventoryItemId}>
                      <Card
                          sx={{
                            maxWidth: 260,
                            overflow: 'hidden',
                            backgroundColor: '#fafafa',
                            display: 'flex',
                            flexDirection: 'column',
                            height: '100%',
                            boxShadow: 3,
                            borderRadius: 2,
                            transition: 'transform 0.2s, box-shadow 0.2s',
                            '&:hover': {
                              transform: 'translateY(-4px)',
                              boxShadow: 6,
                            },
                          }}
                        >
                        <CardMedia
                          component="img"
                          height="180"
                          image={inventoryItem?.imageUrl}
                          alt={inventoryItem?.name || 'Product Image'}
                          sx={{
                            objectFit: 'cover', // Fill card width manteniendo proporción
                            width: '100%',
                            height: '180px',
                            backgroundColor: '#eaeaea',
                            borderTopLeftRadius: 2,
                            borderTopRightRadius: 2,
                          }}
                        />
                        <CardContent sx={{ flexGrow: 1, padding: 2 }}>
                          <Typography variant="h6" sx={{ color: '#333', fontWeight: 'bold' }}>{inventoryItem?.name || 'Unknown Product'}</Typography>
                          <Typography variant="body2" color="text.secondary">
                            Precio: <span style={{ fontWeight: 'bold' }}>₡{inventoryItem?.price.toLocaleString() || 'N/A'}</span>
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            Disponible: <span style={{ fontWeight: 'bold' }}>{inventoryItem?.quantity || 0}</span>
                          </Typography>
                          <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                            <IconButton size="small" disabled>
                              <RemoveIcon />
                            </IconButton>
                            <Typography sx={{ mx: 1 }}>{item.quantity}</Typography>
                            <IconButton size="small" disabled>
                              <AddIcon />
                            </IconButton>
                          </Box>
                          <Typography variant="body1" sx={{ mt: 2, fontWeight: 'bold', display:'flex', justifyContent:'space-between' }}>
                            Total: <span style={{ color: '#007BFF' }}>₡{((inventoryItem?.price || 0) * item.quantity).toLocaleString()}</span>
                          </Typography>
                        </CardContent>
                      </Card>
                    </Grid>
                  );
                })}
              </Grid>
              <Typography variant="h6" sx={{ mt: 2, fontWeight: 'bold', color: '#333' }}>
                Total de la Orden: <span style={{ color: '#007BFF' }}>₡{selectedOrder.order.items.reduce((total, item) => {
                  const inventoryItem = inventoryItems.find(invItem => invItem.id === item.inventoryItemId);
                  return total + (inventoryItem?.price || 0) * item.quantity;
                }, 0).toLocaleString()}</span>
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'center', px: 3, py: 2, backgroundColor: 'rgba(135, 206, 235, 0.1)' }}>
          <Button onClick={handleCloseOrderDetails}
            variant="outlined"
            sx={{
              color: '#87CEEB',
              borderColor: '#87CEEB',
              '&:hover': {
                backgroundColor: 'rgba(135, 206, 235, 0.1)',
                borderColor: '#5CACEE',
              },
              fontWeight: 'bold',
              px: 4,
              py: 1,
            }}>
            Cerrar
          </Button>
        </DialogActions>
      </Dialog>
      {showNotification && (
        <>
          <DarkOverlay />
          <NotificationPopup>
            <AnimatedEmoji style={{ animationDelay: '0s' }}>✨</AnimatedEmoji>
            <Typography
              sx={{
                color: '#1a1a1a',
                fontWeight: 600,
                fontSize: '1.5rem',
                letterSpacing: '-0.02em',
              }}
            >
              Creando Rutas Disponibles
            </Typography>
            <AnimatedEmoji style={{ animationDelay: '0.2s' }}>🎉</AnimatedEmoji>
          </NotificationPopup>
        </>
      )}
      {/* {loadingMessage && (
        <DarkOverlay>
          <NotificationPopup>
            <CircularProgress size={30} sx={{ color: '#87CEEB' }} />
            <Typography sx={{ ml: 2 }}>{loadingMessage}</Typography>
          </NotificationPopup>
        </DarkOverlay>
      )} */}
      {/* <OrderCreationModalAutomatic
        open={isOrderModalAutomatic}
        onClose={() => {
          setIsOrderModalAutomatic(false);
          setEditOrder(null);
        }}
        onOrderCreated={handleOrderCreated}
        inventoryItems={inventoryItems}
        existingOrder={editOrder?.route.order}
        currentRoute={editOrder?.route}
      /> */}

      <Dialog open={isCreateRoutesDialogOpen} onClose={handleCloseCreateRoutesDialog}>
        <DialogTitle sx={{
          backgroundColor: '#87CEEB',
          color: 'white',
          fontWeight: 'bold'
        }}>Seleccionar Rango de Fechas</DialogTitle>
        <DialogContent sx={{ mt: 2, minWidth: 600, px: 4, py: 3 }}>
          <Box sx={{ width: '100%' }}>
            {/* Title */}
            <Typography variant="h5" sx={{
              mb: 4,
              color: 'rgb(0, 180, 216)',
              textAlign: 'center',
              fontWeight: 600,
              letterSpacing: '-0.02em'
            }}>
              Configuración de Rutas Automáticas
            </Typography>

            {/* Info Cards */}
            <Box sx={{ display: 'flex', gap: 3, mb: 4 }}>
              {/* Info Card 1 */}
              <Paper elevation={0} sx={{
                flex: 1,
                p: 3,
                borderRadius: 2,
                backgroundColor: 'rgba(0, 180, 216, 0.04)',
                border: '1px solid rgba(0, 180, 216, 0.1)',
              }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <CalendarTodayIcon sx={{ color: 'rgb(0, 180, 216)', mr: 1 }} />
                  <Typography variant="h6" sx={{ color: 'rgb(0, 180, 216)', fontWeight: 600, fontSize: '1rem' }}>
                    Rango de Fechas
                  </Typography>
                </Box>
                <Typography sx={{
                  color: '#666',
                  fontSize: '0.9rem',
                  lineHeight: 1.6,
                }}>
                  Este rango se utilizará para crear automáticamente las rutas disponibles para los clientes.
                </Typography>
              </Paper>

              {/* Info Card 2 */}
              <Paper elevation={0} sx={{
                flex: 1,
                p: 3,
                borderRadius: 2,
                backgroundColor: 'rgba(0, 180, 216, 0.04)',
                border: '1px solid rgba(0, 180, 216, 0.1)',
              }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <AutorenewIcon sx={{ color: 'rgb(0, 180, 216)', mr: 1 }} />
                  <Typography variant="h6" sx={{ color: 'rgb(0, 180, 216)', fontWeight: 600, fontSize: '1rem' }}>
                    Tipo de Ruta
                  </Typography>
                </Box>
                <Typography sx={{
                  color: '#666',
                  fontSize: '0.9rem',
                  lineHeight: 1.6,
                }}>
                  Las rutas se generarán según el tipo de ruta configurada (semanal, quincenal o mensual).
                </Typography>
              </Paper>
            </Box>

            {/* Date Picker Section */}
            <Box sx={{
              display: 'flex',
              gap: 4,
              justifyContent: 'center',
              alignItems: 'flex-start',
              backgroundColor: '#fff',
              p: 4,
              borderRadius: 2,
              boxShadow: '0 2px 8px rgba(0, 0, 0, 0.05)',
            }}>
              {/* Start Date */}
              <Box sx={{ flex: 1 }}>
                <Typography sx={{
                  mb: 1.5,
                  color: 'rgb(0, 180, 216)',
                  fontWeight: 600,
                  fontSize: '0.95rem'
                }}>
                  Fecha de Inicio
                </Typography>
                <DatePicker
                  selected={routesDateRange[0]}
                  onChange={(date) => {
                    if (date) {
                      setRoutesDateRange([date, routesDateRange[1]]);
                    } else {
                      setRoutesDateRange([null, routesDateRange[1]]);
                    }
                  }}
                  dateFormat="dd/MM/yyyy"
                  customInput={
                    <TextField
                      variant="outlined"
                      fullWidth
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          backgroundColor: '#fff',
                          transition: 'all 0.2s ease',
                          '& fieldset': { borderColor: 'rgba(0, 180, 216, 0.2)' },
                          '&:hover fieldset': { borderColor: 'rgba(0, 180, 216, 0.5)' },
                          '&.Mui-focused fieldset': { borderColor: 'rgb(0, 180, 216)' },
                          input: {
                            color: 'rgb(0, 180, 216)',
                            fontWeight: 500,
                            textAlign: 'center',
                            padding: '12px',
                            fontSize: '0.95rem'
                          }
                        }
                      }}
                    />
                  }
                  locale={es}
                  calendarClassName="custom-calendar"
                />
              </Box>

              {/* End Date */}
              <Box sx={{ flex: 1 }}>
                <Typography sx={{
                  mb: 1.5,
                  color: 'rgb(0, 180, 216)',
                  fontWeight: 600,
                  fontSize: '0.95rem'
                }}>
                  Fecha Final
                </Typography>
                <DatePicker
                  selected={routesDateRange[1]}
                  onChange={(date) => {
                    if (date) {
                      setRoutesDateRange([routesDateRange[0], date]);
                    } else {
                      setRoutesDateRange([routesDateRange[0], null]);
                    }
                  }}
                  dateFormat="dd/MM/yyyy"
                  customInput={
                    <TextField
                      variant="outlined"
                      fullWidth
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          backgroundColor: '#fff',
                          transition: 'all 0.2s ease',
                          '& fieldset': { borderColor: 'rgba(0, 180, 216, 0.2)' },
                          '&:hover fieldset': { borderColor: 'rgba(0, 180, 216, 0.5)' },
                          '&.Mui-focused fieldset': { borderColor: 'rgb(0, 180, 216)' },
                          input: {
                            color: 'rgb(0, 180, 216)',
                            fontWeight: 500,
                            textAlign: 'center',
                            padding: '12px',
                            fontSize: '0.95rem'
                          }
                        }
                      }}
                    />
                  }
                  locale={es}
                  minDate={routesDateRange[0] || undefined}
                  calendarClassName="custom-calendar"
                />
              </Box>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions sx={{
          justifyContent: 'right',
          px: 3,
          py: 2,
          backgroundColor: 'rgba(135, 206, 235, 0.1)'
        }}>
          <Button
            onClick={handleCreateRoutesUnion}
            variant="contained"
            sx={{
              backgroundColor: '#87CEEB',
              color: 'white',
              '&:hover': {
                backgroundColor: '#5CACEE',
              },
              '&:disabled': {
                backgroundColor: 'rgba(135, 206, 235, 0.5)',
                color: 'rgba(255, 255, 255, 0.7)',
              },
              fontWeight: 'bold',
              px: 4,
              py: 1,
            }}>
            Aplicar
          </Button>
          <Button
            onClick={handleCloseCreateRoutesDialog}
            variant="outlined"
            sx={{
              color: '#87CEEB',
              borderColor: '#87CEEB',
              '&:hover': {
                backgroundColor: 'rgba(135, 206, 235, 0.1)',
                borderColor: '#5CACEE',
              },
              fontWeight: 'bold',
              px: 4,
              py: 1,
              mr: 2,
            }}
          >
            Cancelar
          </Button>
        </DialogActions>
      </Dialog>
    </Box >
  );
};

export default OrdersTable;